import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, Plus, Trash2, ChevronDown, ChevronRight, Download, Loader2, Save, Sparkles, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = [
  'Kitchen', 'Primary Bathroom', 'Bathroom', 'Powder Room', 'Primary Bedroom',
  'Bedroom', 'Living Room', 'Family Room', 'Dining Room', 'Office/Study',
  'Laundry Room', 'Mudroom', 'Garage', 'Hallway', 'Closet', 'Basement',
  'Attic', 'Deck/Patio', 'Exterior', 'Mechanical Room', 'Other'
];

export default function MaterialEstimator({ darkMode = false, projectId = null }) {
  const [rooms, setRooms] = useState({});
  const [activeRoom, setActiveRoom] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomType, setNewRoomType] = useState('');
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const saveTimer = useRef(null);

  const bg = darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F8F7F5]';
  const cardBg = darkMode ? 'bg-[#1A1A1E]' : 'bg-white';
  const border = darkMode ? 'border-[#2A2A2A]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-100' : '';

  useEffect(() => {
    if (!projectId) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/design-companion/materials/${projectId}`);
        if (res.data?.rooms && Object.keys(res.data.rooms).length > 0) {
          setRooms(res.data.rooms);
          setActiveRoom(Object.keys(res.data.rooms)[0]);
          setLastSaved(res.data.updated_at);
        }
      } catch {}
    };
    load();
  }, [projectId]);

  // Auto-save
  useEffect(() => {
    if (!projectId || Object.keys(rooms).length === 0) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await axios.put(`${API}/api/design-companion/materials/${projectId}`, { rooms });
        if (res.data?.updated_at) setLastSaved(res.data.updated_at);
      } catch {}
    }, 2000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [rooms, projectId]);

  const addRoom = () => {
    if (!newRoomName.trim()) { toast.error('Enter a room name'); return; }
    const id = `room_${Date.now()}`;
    setRooms(prev => ({
      ...prev,
      [id]: {
        name: newRoomName.trim(),
        type: newRoomType || 'Other',
        dimensions: { length: '', width: '', height: '' },
        scope_notes: '',
        categories: [],
        included: {}
      }
    }));
    setActiveRoom(id);
    setNewRoomName('');
    setNewRoomType('');
    setShowAddRoom(false);
  };

  const removeRoom = (id) => {
    setRooms(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (activeRoom === id) {
      const remaining = Object.keys(rooms).filter(k => k !== id);
      setActiveRoom(remaining[0] || null);
    }
  };

  const updateRoom = (id, field, value) => {
    setRooms(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const updateDimension = (id, dim, value) => {
    setRooms(prev => ({
      ...prev,
      [id]: { ...prev[id], dimensions: { ...prev[id].dimensions, [dim]: value } }
    }));
  };

  const toggleIncluded = (roomId, catIdx, itemIdx) => {
    const key = `${catIdx}_${itemIdx}`;
    setRooms(prev => ({
      ...prev,
      [roomId]: {
        ...prev[roomId],
        included: { ...prev[roomId].included, [key]: !prev[roomId].included?.[key] }
      }
    }));
  };

  const toggleAllCategory = (roomId, catIdx, items) => {
    const allIncluded = items.every((_, i) => rooms[roomId]?.included?.[`${catIdx}_${i}`]);
    setRooms(prev => {
      const newIncluded = { ...prev[roomId].included };
      items.forEach((_, i) => { newIncluded[`${catIdx}_${i}`] = !allIncluded; });
      return { ...prev, [roomId]: { ...prev[roomId], included: newIncluded } };
    });
  };

  const generateEstimate = async () => {
    if (!projectId || !activeRoom) return;
    const room = rooms[activeRoom];
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/design-companion/materials/${projectId}/generate`, {
        room_name: room.name,
        room_type: room.type,
        dimensions: room.dimensions,
        scope_notes: room.scope_notes
      });
      if (res.data?.success && res.data.estimate?.categories) {
        const cats = res.data.estimate.categories;
        const included = {};
        cats.forEach((cat, ci) => cat.items.forEach((_, ii) => { included[`${ci}_${ii}`] = true; }));
        updateRoom(activeRoom, 'categories', cats);
        updateRoom(activeRoom, 'included', included);
        setExpandedCats({});
        toast.success(`Generated ${cats.length} categories for ${room.name}`);
      } else {
        toast.error(res.data?.error || 'Failed to generate estimate');
      }
    } catch (e) {
      toast.error('Failed to generate estimate');
    }
    setGenerating(false);
  };

  const exportCSV = () => {
    let csv = 'Room,Category,Material,Quantity,Unit,In Scope,Notes\n';
    Object.values(rooms).forEach(room => {
      (room.categories || []).forEach((cat, ci) => {
        cat.items.forEach((item, ii) => {
          const inScope = room.included?.[`${ci}_${ii}`] ? 'Yes' : 'No';
          csv += `"${room.name}","${cat.name}","${item.material}",${item.quantity},"${item.unit}","${inScope}","${item.notes || ''}"\n`;
        });
      });
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `material_estimate_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const room = activeRoom ? rooms[activeRoom] : null;
  const roomKeys = Object.keys(rooms);

  const getScopeSummary = () => {
    let totalItems = 0, includedItems = 0;
    Object.values(rooms).forEach(r => {
      (r.categories || []).forEach((cat, ci) => {
        cat.items.forEach((_, ii) => {
          totalItems++;
          if (r.included?.[`${ci}_${ii}`]) includedItems++;
        });
      });
    });
    return { totalItems, includedItems };
  };

  const summary = getScopeSummary();

  return (
    <div className={`p-6 space-y-5 ${bg}`} data-testid="material-estimator">
      <div>
        <div className="flex items-center justify-between">
          <h2 className={`text-lg font-semibold mb-1 ${text}`}>Material Quantity Estimator</h2>
          <div className="flex items-center gap-2">
            {lastSaved && (
              <div className={`flex items-center gap-1 text-[10px] ${textMuted}`}>
                <Save className="w-3 h-3" /> Saved {new Date(lastSaved).toLocaleTimeString()}
              </div>
            )}
            {summary.totalItems > 0 && (
              <Button variant="outline" size="sm" onClick={exportCSV} className="text-xs gap-1" data-testid="material-export-csv">
                <Download className="w-3 h-3" /> Export CSV
              </Button>
            )}
          </div>
        </div>
        <p className={`text-sm ${textMuted}`}>
          AI-powered material takeoffs by room. Generate comprehensive quantity lists, then select what's in scope for your estimate.
        </p>
        {!projectId && (
          <div className={`mt-2 text-[11px] ${textMuted} italic`}>Select a project to use the Material Estimator</div>
        )}
      </div>

      {projectId && (
        <div className="flex gap-4">
          {/* Room sidebar */}
          <div className={`w-52 flex-shrink-0 space-y-1`}>
            {roomKeys.map(id => (
              <button
                key={id}
                onClick={() => setActiveRoom(id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between group transition-colors
                  ${activeRoom === id
                    ? `${darkMode ? 'bg-[#BC5D41]/15 text-[#E8A590]' : 'bg-[#BC5D41]/10 text-[#70170F]'} font-medium`
                    : `${darkMode ? 'text-gray-300 hover:bg-[#1A1A1E]' : 'text-gray-600 hover:bg-gray-100'}`
                  }`}
                data-testid={`room-tab-${id}`}
              >
                <span className="truncate">{rooms[id].name}</span>
                <span className={`text-[10px] ${textMuted}`}>{rooms[id].type}</span>
              </button>
            ))}
            {showAddRoom ? (
              <div className={`p-3 rounded-lg border ${border} ${cardBg} space-y-2`}>
                <Input
                  placeholder="Room name..."
                  value={newRoomName}
                  onChange={e => setNewRoomName(e.target.value)}
                  className={`text-sm h-8 ${inputBg}`}
                  data-testid="new-room-name"
                  onKeyDown={e => e.key === 'Enter' && addRoom()}
                  autoFocus
                />
                <Select value={newRoomType} onValueChange={setNewRoomType}>
                  <SelectTrigger className={`h-8 text-xs ${inputBg}`}><SelectValue placeholder="Room type..." /></SelectTrigger>
                  <SelectContent>
                    {ROOM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-1">
                  <Button size="sm" onClick={addRoom} className="flex-1 h-7 text-xs bg-[#BC5D41] hover:bg-[#70170F] text-white">Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowAddRoom(false)} className="h-7 text-xs"><X className="w-3 h-3" /></Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddRoom(true)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${darkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-[#1A1A1E]' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                data-testid="add-room-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add Room
              </button>
            )}

            {/* Scope summary */}
            {summary.totalItems > 0 && (
              <div className={`mt-4 p-3 rounded-lg border ${border} ${cardBg}`}>
                <div className={`text-[10px] font-medium uppercase tracking-wider ${textMuted} mb-2`}>Scope Summary</div>
                <div className={`text-lg font-bold ${text}`}>{summary.includedItems}<span className={`text-sm font-normal ${textMuted}`}>/{summary.totalItems} items</span></div>
                <div className={`text-[10px] ${textMuted}`}>across {roomKeys.length} room{roomKeys.length !== 1 ? 's' : ''}</div>
              </div>
            )}
          </div>

          {/* Room detail panel */}
          <div className="flex-1 min-w-0">
            {!room ? (
              <div className={`${cardBg} border ${border} rounded-xl p-12 text-center`}>
                <Package className={`w-10 h-10 mx-auto mb-3 ${textMuted} opacity-30`} />
                <p className={textMuted}>Add a room to start estimating materials</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Room header */}
                <div className={`${cardBg} border ${border} rounded-xl p-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`font-semibold ${text}`}>{room.name} <span className={`text-sm font-normal ${textMuted}`}>({room.type})</span></h3>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeRoom(activeRoom)}
                        className="h-7 text-xs text-red-400 hover:text-red-300"
                        data-testid="remove-room-btn"
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <label className={`text-[10px] ${textMuted}`}>Length (ft)</label>
                      <Input
                        type="number" value={room.dimensions?.length || ''} placeholder="—"
                        onChange={e => updateDimension(activeRoom, 'length', e.target.value)}
                        className={`h-8 text-sm mt-0.5 ${inputBg}`} data-testid="dim-length"
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] ${textMuted}`}>Width (ft)</label>
                      <Input
                        type="number" value={room.dimensions?.width || ''} placeholder="—"
                        onChange={e => updateDimension(activeRoom, 'width', e.target.value)}
                        className={`h-8 text-sm mt-0.5 ${inputBg}`} data-testid="dim-width"
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] ${textMuted}`}>Ceiling (ft)</label>
                      <Input
                        type="number" value={room.dimensions?.height || ''} placeholder="9"
                        onChange={e => updateDimension(activeRoom, 'height', e.target.value)}
                        className={`h-8 text-sm mt-0.5 ${inputBg}`} data-testid="dim-height"
                      />
                    </div>
                    <div>
                      <label className={`text-[10px] ${textMuted}`}>Scope Notes</label>
                      <Input
                        value={room.scope_notes || ''} placeholder="e.g., full gut, keep plumbing..."
                        onChange={e => updateRoom(activeRoom, 'scope_notes', e.target.value)}
                        className={`h-8 text-sm mt-0.5 ${inputBg}`} data-testid="scope-notes"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Button
                      onClick={generateEstimate}
                      disabled={generating}
                      className="bg-[#BC5D41] hover:bg-[#70170F] text-white gap-1.5"
                      data-testid="generate-materials-btn"
                    >
                      {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {generating ? 'Generating...' : room.categories?.length ? 'Regenerate Materials' : 'Generate Material List'}
                    </Button>
                  </div>
                </div>

                {/* Material categories */}
                {(room.categories || []).map((cat, ci) => {
                  const expanded = expandedCats[ci] !== false;
                  const catItems = cat.items || [];
                  const includedCount = catItems.filter((_, ii) => room.included?.[`${ci}_${ii}`]).length;
                  return (
                    <div key={ci} className={`${cardBg} border ${border} rounded-xl overflow-hidden`}>
                      <button
                        onClick={() => setExpandedCats(prev => ({ ...prev, [ci]: !expanded }))}
                        className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-black/5 transition-colors`}
                        data-testid={`cat-header-${ci}`}
                      >
                        <div className="flex items-center gap-2">
                          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className={`font-medium text-sm ${text}`}>{cat.name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            includedCount === catItems.length
                              ? 'bg-green-500/10 text-green-500'
                              : includedCount > 0
                                ? 'bg-amber-500/10 text-amber-500'
                                : `${darkMode ? 'bg-[#333]' : 'bg-gray-100'} ${textMuted}`
                          }`}>{includedCount}/{catItems.length}</span>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); toggleAllCategory(activeRoom, ci, catItems); }}
                          className={`text-[10px] px-2 py-0.5 rounded border ${border} ${textMuted} hover:text-[#BC5D41] transition-colors`}
                          data-testid={`toggle-all-${ci}`}
                        >
                          {includedCount === catItems.length ? 'Exclude All' : 'Include All'}
                        </button>
                      </button>
                      {expanded && (
                        <div className="px-4 pb-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={`text-[10px] uppercase tracking-wider ${textMuted} border-b ${border}`}>
                                <th className="text-left pb-2 w-6"></th>
                                <th className="text-left pb-2">Material</th>
                                <th className="text-right pb-2 w-16">Qty</th>
                                <th className="text-left pb-2 w-12 pl-2">Unit</th>
                                <th className="text-left pb-2">Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {catItems.map((item, ii) => {
                                const included = room.included?.[`${ci}_${ii}`];
                                return (
                                  <tr key={ii} className={`border-b last:border-0 ${border} ${!included ? 'opacity-40' : ''}`}>
                                    <td className="py-1.5 pr-1">
                                      <button
                                        onClick={() => toggleIncluded(activeRoom, ci, ii)}
                                        className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                          included ? 'bg-[#BC5D41] border-[#BC5D41] text-white' : `${border}`
                                        }`}
                                        data-testid={`toggle-item-${ci}-${ii}`}
                                      >
                                        {included && <Check className="w-3 h-3" />}
                                      </button>
                                    </td>
                                    <td className={`py-1.5 ${text} text-xs`}>{item.material}</td>
                                    <td className={`py-1.5 text-right font-mono text-xs ${text}`}>{item.quantity}</td>
                                    <td className={`py-1.5 pl-2 text-xs ${textMuted}`}>{item.unit}</td>
                                    <td className={`py-1.5 text-xs ${textMuted}`}>{item.notes || ''}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
